import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const PageManagementPanel = () => {
  const [activeSubTab, setActiveSubTab] = useState('pages');

  const subTabs = [
    { id: 'pages', label: 'Pages', icon: 'FileText' },
    { id: 'menu', label: 'Menu', icon: 'Menu' },
    { id: 'content', label: 'Content', icon: 'Edit' }
  ];

  const mockPages = [
    { id: 1, title: 'About Us', slug: 'about-us', status: 'published', lastModified: '2024-01-15' },
    { id: 2, title: 'Contact Us', slug: 'contact-us', status: 'published', lastModified: '2024-01-14' },
    { id: 3, title: 'Privacy Policy', slug: 'privacy-policy', status: 'published', lastModified: '2024-01-10' },
    { id: 4, title: 'Terms of Service', slug: 'terms-of-service', status: 'published', lastModified: '2024-01-08' },
    { id: 5, title: 'FAQ', slug: 'faq', status: 'draft', lastModified: '2024-01-12' }
  ];

  const renderSubTabContent = () => {
    switch (activeSubTab) {
      case 'pages':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-soft p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
                <h3 className="font-heading font-semibold text-lg text-text-primary">
                  Manage Pages
                </h3>
                <Button variant="primary" iconName="Plus" iconSize={16}>
                  Add Page
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <Input placeholder="Search pages..." icon="Search" />
                <select className="px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option>All Status</option>
                  <option>Published</option>
                  <option>Draft</option>
                  <option>Archived</option>
                </select>
                <select className="px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option>All Types</option>
                  <option>Static Page</option>
                  <option>Landing Page</option>
                  <option>Legal Page</option>
                </select>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-surface-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                        Page Title
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                        Slug
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                        Last Modified
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {mockPages.map((page) => (
                      <tr key={page.id} className="hover:bg-surface-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                              <Icon name="FileText" size={16} className="text-primary" />
                            </div>
                            <span className="font-medium text-text-primary">{page.title}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-text-secondary">/{page.slug}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            page.status === 'published' ? 'bg-success-100 text-success' : 
                            page.status === 'draft'? 'bg-warning-100 text-warning' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {page.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-text-secondary">{page.lastModified}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center space-x-2">
                            <button className="text-primary hover:text-primary-600">
                              <Icon name="Edit" size={16} />
                            </button>
                            <button className="text-gray-500 hover:text-gray-700">
                              <Icon name="Eye" size={16} />
                            </button>
                            <button className="text-red-500 hover:text-red-700">
                              <Icon name="Trash2" size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      case 'menu':
        return (
          <div className="bg-white rounded-xl shadow-soft p-6">
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Menu" size={32} className="text-primary" />
              </div>
              <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
                Menu Management
              </h3>
              <p className="text-text-secondary mb-6">
                Configure navigation menus and structure
              </p>
              <Button variant="outline" disabled>
                Manage Menus
              </Button>
            </div>
          </div>
        );
      case 'content':
        return (
          <div className="bg-white rounded-xl shadow-soft p-6">
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Edit" size={32} className="text-primary" />
              </div>
              <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
                Content Editor
              </h3>
              <p className="text-text-secondary mb-6">
                Rich text editor for page content management
              </p>
              <Button variant="outline" disabled>
                Open Editor
              </Button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-primary to-secondary rounded-xl p-6 text-white">
        <h2 className="font-heading font-bold text-2xl mb-2">
          Page Management
        </h2>
        <p className="text-white/90">
          Manage website pages, menus, and content
        </p>
      </div>

      {/* Sub Navigation */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <div className="flex flex-wrap gap-2">
          {subTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                activeSubTab === tab.id
                  ? 'bg-primary text-white' :'text-text-secondary hover:bg-surface-100'
              }`}
            >
              <Icon name={tab.icon} size={16} />
              <span className="font-medium">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Sub Tab Content */}
      {renderSubTabContent()}
    </div>
  );
};

export default PageManagementPanel;